import { isAxiosError } from 'axios';
import classNames from 'classnames';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import InputMask from 'react-input-mask';
import { toast } from 'react-toastify';

import { CommonApi, type PatientDTO, PeopleApi } from '../../api';
import type { PatientContact } from '../../model';
import { normalizePhoneNumber, patientFormValidation } from '../../utils';
import { Modal } from '../molecules';

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

type CreatePatientForm = Pick<PatientDTO, 'username'>;

const PLACEHOLDER_SURNAME = 'Клиент';

export const CreatePatientModal = ({ isOpen, onClose }: Props) => {
  const queryClient = useQueryClient();

  const {
    control,
    formState: { errors },
    handleSubmit,
    reset,
  } = useForm<CreatePatientForm>({
    defaultValues: { username: '' },
  });

  const { data: genderList = [] } = useQuery({
    queryKey: [CommonApi.KEY, 'getReference', 107],
    queryFn: () => CommonApi.getReference(107),
    enabled: isOpen,
  });

  const { mutate: createPatient, isLoading: isPending } = useMutation({
    mutationFn: PeopleApi.createPatient,
    onSuccess() {
      queryClient.invalidateQueries({ queryKey: [PeopleApi.KEY] });
      toast.success('Клиент успешно добавлен');
      reset({ username: '' });
      onClose();
    },
    onError(error: unknown) {
      if (isAxiosError(error)) {
        const data = error.response?.data as
          | { message?: string; error?: string }
          | undefined;
        const msg = data?.message ?? data?.error;
        toast.error(
          typeof msg === 'string' && msg.trim()
            ? msg
            : 'Ошибка! Проверьте данные',
        );
        return;
      }
      toast.error('Ошибка! Проверьте данные');
    },
  });

  useEffect(() => {
    if (!isOpen) {
      reset({ username: '' });
    }
  }, [isOpen, reset]);

  const onSubmit: SubmitHandler<CreatePatientForm> = useCallback(
    (data) => {
      const phone = normalizePhoneNumber(data.username);
      const genderId = genderList[0]?.id ?? 1;
      const body: PatientDTO = {
        surname: PLACEHOLDER_SURNAME,
        name: '',
        midname: '',
        gender_id: genderId,
        birthdate: null,
        code: '',
        email: '',
        username: phone,
        contacts: [
          {
            contact_type: { id: 18 } as PatientContact['contact_type'],
            value: phone,
          },
        ],
        documents: [],
      };
      createPatient(body);
    },
    [createPatient, genderList],
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="flex max-h-[90vh] min-h-[300px] flex-col overflow-hidden rounded-[16px] bg-white p-[24px]">
        <h2 className="mb-[32px] text-2xl font-bold text-[#151515]">
          Добавить пациента
        </h2>
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex grow flex-col overflow-auto"
        >
          <div className="mb-[24px]">
            <Controller
              control={control}
              name="username"
              rules={patientFormValidation.username}
              render={({ field, fieldState }) => (
                <InputMask
                  {...field}
                  alwaysShowMask={false}
                  className={classNames(
                    'input input--block',
                    fieldState.invalid && 'input--invalid',
                  )}
                  mask="+7 999 999 99 99"
                  maskPlaceholder={null}
                  placeholder="Телефон"
                  type="tel"
                  value={field.value ?? ''}
                />
              )}
            />
            {errors.username && (
              <p className="text-red-500 mt-[10px] text-sm">
                {errors.username.message}
              </p>
            )}
          </div>

          <div className="mt-auto flex gap-[40px] pt-[16px]">
            <button
              type="button"
              onClick={onClose}
              className="button--secondary button flex-1"
            >
              Отмена
            </button>
            <button
              className="button button--primary flex-1"
              type="submit"
              disabled={isPending}
            >
              {isPending ? 'Добавление...' : 'Добавить пациента'}
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
};
